import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { cn } from "lib/utils";
import { X } from "lucide-react";
import { useTransitionState } from "./useTransitionState";

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  side?: "right" | "left";
  showCloseButton?: boolean;
  className?: string;
  /** Stacking level of the whole sheet (overlay + panel). Defaults to 1300. */
  zIndex?: number;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  /** Marks which wizard step this sheet belongs to for Copilot control mode. */
  "data-copilot-step"?: string;
  children: React.ReactNode;
}

const Sheet = ({
  open,
  onClose,
  side = "right",
  showCloseButton = true,
  className,
  zIndex,
  children,
  ...ariaProps
}: SheetProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const { mounted, entered } = useTransitionState(open);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const panel = panelRef.current;
    panel?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab" || !panel) {
        return;
      }
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusables.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!mounted || typeof document === "undefined") {
    return null;
  }

  // Animate via inline styles rather than Tailwind utilities so it never depends
  // on those classes being emitted into the generated stylesheet.
  const closedTransform = side === "right" ? "translateX(100%)" : "translateX(-100%)";

  return createPortal(
    <div className="fixed inset-0 z-[1300]" style={zIndex !== undefined ? { zIndex } : undefined}>
      <div
        className="absolute inset-0"
        style={{
          backgroundColor: "rgba(0,0,0,0.5)",
          opacity: entered ? 1 : 0,
          transition: "opacity 300ms ease-in-out",
        }}
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaProps["aria-label"]}
        aria-labelledby={ariaProps["aria-labelledby"]}
        data-copilot-step={ariaProps["data-copilot-step"]}
        tabIndex={-1}
        style={{
          transform: entered ? "translateX(0)" : closedTransform,
          transition: "transform 300ms ease-in-out",
        }}
        className={cn(
          "absolute top-0 flex h-full w-full max-w-md flex-col bg-background text-foreground shadow-lg focus:outline-none",
          side === "right" ? "right-0" : "left-0",
          className
        )}
      >
        {showCloseButton && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100"
          >
            <X className="h-5 w-5" />
          </button>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
};

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col gap-1 border-b border-border p-6", className)} {...props} />
);

const SheetTitle = ({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h2 className={cn("text-lg font-semibold text-foreground", className)} {...props} />
);

const SheetDescription = ({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cn("text-sm text-muted-foreground", className)} {...props} />
);

const SheetBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex-1 overflow-y-auto p-6", className)} {...props} />
);

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("border-t border-border p-6", className)} {...props} />
);

export { Sheet, SheetHeader, SheetTitle, SheetDescription, SheetBody, SheetFooter };
